// The delete dialog's failure copy (I4, PO ruling 2026-10-27). The in-use
// refusal is stated from its counts, in our own words with correct singular
// and plural: the API's sentence is never shown.
import { asApiFailure } from "@utils/apiFailure";
import { categoryInUse, type CategoryInUse } from "@models/category";

const noun = (count: number, one: string, many: string): string =>
    count === 1 ? one : many;

const inUseMessage = (
    name: string,
    { productCount, subcategoryCount }: CategoryInUse,
): string => {
    // Each word is chosen once, then used both after its count and in the
    // instruction that ends the sentence, so the two can't disagree.
    const products = noun(productCount, "product", "products");
    const subcategories = noun(
        subcategoryCount,
        "subcategory",
        "subcategories",
    );
    // productCount includes inactive products: they still hold the category,
    // and the admin must look for them with "include inactive" on.
    const productUse = `${productCount} ${products} (inactive products included)`;
    const subcategoryUse = `${subcategoryCount} ${subcategories}`;

    if (productCount > 0 && subcategoryCount > 0) {
        return `${name} can't be deleted: it has ${productUse} and ${subcategoryUse}. Move the ${products} to another category, and move or delete the ${subcategories}, first.`;
    }

    if (productCount > 0) {
        return `${name} can't be deleted: it has ${productUse}. Move the ${products} to another category first.`;
    }

    return `${name} can't be deleted: it has ${subcategoryUse}. Move or delete the ${subcategories} first.`;
};

// Any other failure. useApi has usually toasted it already (an HTTP or
// network failure, or the session-expired warning), but not always: a request
// that outlived its session, or whose access token failed for any reason
// other than an expired session, gets no toast. Retrying is safe, as the
// delete is idempotent.
const failedMessage = (name: string): string =>
    `${name} couldn't be deleted. Please try again.`;

export const deleteCategoryErrorMessage = (
    name: string,
    error: unknown,
): string => {
    const failure = asApiFailure(error);
    const inUse =
        failure?.status === 409 ? categoryInUse(failure.detail) : undefined;

    return inUse ? inUseMessage(name, inUse) : failedMessage(name);
};
